import styled from 'styled-components'

import { Layout, Text } from '@loadsmart/miranda-react'
import { getToken } from '@loadsmart/miranda-react/dist/tokens'

import { TrackingProvider } from '../../../domain/Tracking'
import { formatTrackingProviderName } from '../../../presenters/preferredTrackingMethod'
import ImageP44 from '../../assets/tracking-onboarding-finalize-connection-p44.png'
import ImageTruckerTools from '../../assets/tracking-onboarding-finalize-connection-tt.png'
import { useTrackingOnboardingContext } from './TrackingOnboardingContext'

const StyledListItem = styled.li`
  padding-bottom: ${getToken('spacing-2')};
  list-style: decimal;
`

export type ELDFinalizeConnectionContentProps = {
  readonly selectedProvider?: TrackingProvider
}

export default function ELDFinalizeConnectionContent({
  selectedProvider,
}: ELDFinalizeConnectionContentProps) {
  const { hasPreviousTracking } = useTrackingOnboardingContext()
  const isP44 = selectedProvider === TrackingProvider.P44

  if (hasPreviousTracking) {
    const formatterName = formatTrackingProviderName(selectedProvider)

    return (
      <Layout.Group style={{ flexWrap: 'nowrap' }} gap="spacing-12">
        <Layout.Stack gap="spacing-4">
          <Text variant="body-md-bold">ELD with {formatterName}</Text>

          <Layout.Stack gap="spacing-6">
            <Text variant="body-md">
              You're about to confirm your preferred connection as {formatterName} ELD.
            </Text>
            <Text variant="body-md">You can update this choice anytime via the tracking card.</Text>
          </Layout.Stack>
        </Layout.Stack>
        <img src={isP44 ? ImageP44 : ImageTruckerTools} alt="" width={220} height={250} />
      </Layout.Group>
    )
  }

  if (isP44) {
    return (
      <Layout.Group style={{ flexWrap: 'nowrap' }} gap="spacing-12">
        <Layout.Stack gap="spacing-4">
          <Text variant="body-md-bold">Connect to Project44 ELDs</Text>
          <Text variant="body-md">
            When you connect with your telematics account, a single integration enables you to
            seamlessly send location data from your GPS devices, use the Project44 setup to complete
            it and get it ready to track your loads.
          </Text>
          <Layout.Group paddingL="spacing-4">
            <Text variant="body-md">
              <ol>
                <StyledListItem>You will be redirected to Project44 ELD setup page</StyledListItem>
                <StyledListItem>
                  Inform your credentials and access your account setup
                </StyledListItem>
                <StyledListItem>
                  Confirm the carrier information and finalize the connection with us
                </StyledListItem>
                <StyledListItem>
                  Once your are done, we will be checking the connection, as soon as we got the
                  first ping, we will set your tracking method as connected.
                </StyledListItem>
              </ol>
            </Text>
          </Layout.Group>
        </Layout.Stack>
        <img src={ImageP44} alt="" width={220} height={250} />
      </Layout.Group>
    )
  }

  return (
    <Layout.Group style={{ flexWrap: 'nowrap' }} gap="spacing-12">
      <Layout.Stack gap="spacing-4">
        <Text variant="body-md-bold">Connect to TruckerTools ELDs</Text>
        <Text variant="body-md">
          When you connect with your telematics account, a single integration enables you to
          seamlessly send location data from your GPS devices, use the TruckerTools setup to
          complete it and get it ready to track your loads.
        </Text>
        <Layout.Group paddingL="spacing-4">
          <Text variant="body-md">
            <ol>
              <StyledListItem>You will be redirected to TruckerTools ELD setup page</StyledListItem>
              <StyledListItem>
                Enter your information and submit the connection request
              </StyledListItem>
              <StyledListItem>
                After submitting the request, someone from TruckerTools will reach out to you and
                complete the connection.
              </StyledListItem>
            </ol>
          </Text>
        </Layout.Group>
      </Layout.Stack>
      <img src={ImageTruckerTools} alt="" width={220} height={250} />
    </Layout.Group>
  )
}
